import { useMemo, useState } from "react";
import TriStateControl, { isUnset } from "./TriStateControl";
import OsVersionBadges from "./OsVersionBadges";

const EAP_TYPES = [
  { value: 13, label: "TLS" },
  { value: 17, label: "LEAP" },
  { value: 18, label: "EAP-SIM" },
  { value: 21, label: "TTLS" },
  { value: 23, label: "EAP-AKA" },
  { value: 25, label: "PEAP" },
  { value: 43, label: "EAP-FAST" },
];

const ENCRYPTION = [
  { value: "WPA3", label: "WPA3" },
  { value: "WPA2", label: "WPA2" },
  { value: "WPA", label: "WPA" },
  { value: "Any", label: "Beliebig (WPA/WPA2/WPA3)" },
  { value: "WEP", label: "WEP" },
  { value: "None", label: "Offen" },
];

function BoolRow({ label, hint, value, onChange, disabled, introduced }) {
  return (
    <div className="mb-2">
      <div className="profile-field-row">
        <span className="profile-field-label">
          {label}
          <OsVersionBadges introduced={introduced} />
        </span>
        <TriStateControl value={value} onChange={onChange} disabled={disabled} name={label} />
      </div>
      {hint ? <div className="form-text">{hint}</div> : null}
    </div>
  );
}

function cleanEap(eap) {
  const next = { ...(eap || {}) };
  if (!Array.isArray(next.AcceptEAPTypes) || next.AcceptEAPTypes.length === 0) return undefined;
  for (const key of Object.keys(next)) {
    if (next[key] === "" || next[key] === undefined || next[key] === null) delete next[key];
  }
  if (Array.isArray(next.TLSTrustedServerNames) && next.TLSTrustedServerNames.length === 0) {
    delete next.TLSTrustedServerNames;
  }
  if (Array.isArray(next.PayloadCertificateAnchorUUID) && next.PayloadCertificateAnchorUUID.length === 0) {
    delete next.PayloadCertificateAnchorUUID;
  }
  return next;
}

export default function WifiPayloadEditor({ fields, onChange, disabled, identityCerts = [], trustCerts = [] }) {
  const [tab, setTab] = useState("protocols");
  const encryption = fields?.EncryptionType || "WPA2";
  const enterprise = Boolean(fields?.enterpriseMode) || Boolean(fields?.EAPClientConfiguration);
  const eap = fields?.EAPClientConfiguration || { AcceptEAPTypes: [] };
  const accepted = Array.isArray(eap.AcceptEAPTypes) ? eap.AcceptEAPTypes.map(Number) : [];

  const identityOptions = useMemo(
    () => identityCerts.filter((item) => item.uuid),
    [identityCerts],
  );

  const patch = (partial) => onChange({ ...fields, ...partial });
  const patchEap = (partial) => {
    const next = cleanEap({ ...eap, ...partial });
    patch({ EAPClientConfiguration: next, enterpriseMode: next ? true : undefined });
  };

  return (
    <div>
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Netzwerk-SSID</label>
          <input
            className="form-control"
            disabled={disabled}
            value={fields?.SSID_STR || ""}
            onChange={(e) => patch({ SSID_STR: e.target.value || undefined })}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Verschlüsselung</label>
          <select
            className="form-select"
            disabled={disabled}
            value={encryption}
            onChange={(e) => patch({ EncryptionType: e.target.value })}
          >
            {ENCRYPTION.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <BoolRow
            label="Automatisch verbinden"
            value={fields?.AutoJoin}
            onChange={(value) => patch({ AutoJoin: value })}
            disabled={disabled}
            introduced={{ iOS: "5.0" }}
          />
        </div>
        <div className="col-md-6">
          <BoolRow
            label="Unsichtbares Netzwerk"
            value={fields?.HIDDEN_NETWORK}
            onChange={(value) => patch({ HIDDEN_NETWORK: value })}
            disabled={disabled}
          />
        </div>
        <div className="col-md-6">
          <BoolRow
            label="Captive-Portal-Erkennung deaktivieren"
            value={fields?.CaptiveBypass}
            onChange={(value) => patch({ CaptiveBypass: value })}
            disabled={disabled}
            introduced={{ iOS: "10.0" }}
          />
        </div>
        <div className="col-md-6">
          <BoolRow
            label="MAC-Adressen-Randomisierung deaktivieren"
            hint="Zeigt in den Geräteeinstellungen einen Hinweis zu eingeschränktem Datenschutz."
            value={fields?.DisableAssociationMACRandomization}
            onChange={(value) => patch({ DisableAssociationMACRandomization: value })}
            disabled={disabled}
            introduced={{ iOS: "14.0", macOS: "15.0" }}
          />
        </div>
        {encryption !== "None" && !enterprise ? (
          <div className="col-md-6">
            <label className="form-label">Passwort</label>
            <input
              type="password"
              className="form-control"
              disabled={disabled}
              placeholder="Nicht konfiguriert"
              value={fields?.Password || ""}
              onChange={(e) => patch({ Password: e.target.value || undefined })}
            />
          </div>
        ) : null}
        <div className="col-12">
          <label className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              disabled={disabled || encryption === "None"}
              checked={enterprise}
              onChange={(e) => {
                if (e.target.checked) {
                  patch({
                    enterpriseMode: true,
                    Password: undefined,
                    EAPClientConfiguration: eap.AcceptEAPTypes?.length ? eap : { AcceptEAPTypes: [13] },
                  });
                } else {
                  patch({ enterpriseMode: undefined, EAPClientConfiguration: undefined, PayloadCertificateUUID: undefined });
                }
              }}
            />
            <span className="form-check-label">
              Enterprise (802.1X / EAP) — Zertifikat und Protokolle statt PSK
            </span>
          </label>
          <div className="form-text">
            Für Return to Service müssen PKCS#12-Identität und Stammzertifikate <strong>im selben</strong> Profil
            liegen — das gesamte Profil wird als WiFiProfileData mitgeschickt.
          </div>
        </div>
      </div>

      {enterprise && encryption !== "None" ? (
        <div className="border rounded p-3 mt-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <strong>WPA-Enterprise</strong>
            <div className="btn-group" role="group">
              <button type="button" className={`btn btn-sm ${tab === "protocols" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setTab("protocols")}>
                Protokolle
              </button>
              <button type="button" className={`btn btn-sm ${tab === "trust" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setTab("trust")}>
                Vertrauenswürdigkeit
              </button>
            </div>
          </div>

          {tab === "protocols" ? (
            <div className="row g-3">
              <div className="col-12">
                <div className="form-label mb-1">Unterstützte EAP-Typen</div>
                <div className="d-flex flex-wrap gap-3">
                  {EAP_TYPES.map((type) => (
                    <label key={type.value} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        disabled={disabled}
                        checked={accepted.includes(type.value)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...accepted, type.value]
                            : accepted.filter((value) => value !== type.value);
                          patchEap({ AcceptEAPTypes: next });
                        }}
                      />
                      <span className="form-check-label">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Benutzername</label>
                <input
                  className="form-control"
                  disabled={disabled}
                  placeholder="optional, sonst Eingabe am Gerät"
                  value={eap.UserName || ""}
                  onChange={(e) => patchEap({ UserName: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Identitätszertifikat</label>
                <select
                  className="form-select"
                  disabled={disabled}
                  value={fields?.PayloadCertificateUUID || ""}
                  onChange={(e) => patch({ PayloadCertificateUUID: e.target.value || undefined })}
                >
                  <option value="">Nicht zugewiesen</option>
                  {identityOptions.map((cert) => (
                    <option key={cert.uuid} value={cert.uuid}>
                      {cert.name} ({cert.uuid.slice(0, 8)}…)
                    </option>
                  ))}
                </select>
                {identityOptions.length === 0 ? (
                  <div className="form-text">Zuerst einen PKCS#12-Payload in diesem Profil hinzufügen.</div>
                ) : null}
              </div>
              <div className="col-md-6">
                <label className="form-label d-inline-flex align-items-center gap-1">
                  Min. TLS-Version <OsVersionBadges introduced={{ iOS: "11.0" }} />
                </label>
                <select
                  className="form-select"
                  disabled={disabled}
                  value={eap.TLSMinimumVersion || ""}
                  onChange={(e) => patchEap({ TLSMinimumVersion: e.target.value })}
                >
                  <option value="">Nicht konfiguriert</option>
                  <option value="1.0">1.0</option>
                  <option value="1.1">1.1</option>
                  <option value="1.2">1.2</option>
                  <option value="1.3">1.3</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label d-inline-flex align-items-center gap-1">
                  Max. TLS-Version <OsVersionBadges introduced={{ iOS: "11.0" }} />
                </label>
                <select
                  className="form-select"
                  disabled={disabled}
                  value={eap.TLSMaximumVersion || ""}
                  onChange={(e) => patchEap({ TLSMaximumVersion: e.target.value })}
                >
                  <option value="">Nicht konfiguriert</option>
                  <option value="1.0">1.0</option>
                  <option value="1.1">1.1</option>
                  <option value="1.2">1.2</option>
                  <option value="1.3">1.3</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Äußere Identität</label>
                <input
                  className="form-control"
                  disabled={disabled}
                  placeholder="z. B. anonymous@schule.de"
                  value={eap.OuterIdentity || ""}
                  onChange={(e) => patchEap({ OuterIdentity: e.target.value })}
                />
              </div>
              {accepted.includes(21) ? (
                <div className="col-md-6">
                  <label className="form-label">TTLS Inner Authentication</label>
                  <select
                    className="form-select"
                    disabled={disabled}
                    value={eap.TTLSInnerAuthentication || "MSCHAPv2"}
                    onChange={(e) => patchEap({ TTLSInnerAuthentication: e.target.value })}
                  >
                    {["PAP", "CHAP", "MSCHAP", "MSCHAPv2", "EAP"].map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Vertrauenswürdige Servernamen (eine pro Zeile)</label>
                <textarea
                  className="form-control font-monospace small"
                  rows={3}
                  disabled={disabled}
                  value={(eap.TLSTrustedServerNames || []).join("\n")}
                  onChange={(e) => patchEap({
                    TLSTrustedServerNames: e.target.value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean),
                  })}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Anker-Zertifikate in diesem Profil</label>
                {trustCerts.length === 0 ? (
                  <div className="form-text">Stammzertifikate aus diesem Profil erscheinen hier zur Zuweisung.</div>
                ) : (
                  trustCerts.map((cert) => {
                    const selected = (eap.PayloadCertificateAnchorUUID || []).includes(cert.uuid);
                    return (
                      <label key={cert.uuid} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          disabled={disabled || !cert.uuid}
                          checked={selected}
                          onChange={(e) => {
                            const current = eap.PayloadCertificateAnchorUUID || [];
                            patchEap({
                              PayloadCertificateAnchorUUID: e.target.checked
                                ? [...current, cert.uuid]
                                : current.filter((id) => id !== cert.uuid),
                            });
                          }}
                        />
                        <span className="form-check-label">{cert.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}

      <div className="row g-3 mt-1">
        <div className="col-md-6">
          <label className="form-label">Proxy</label>
          <select
            className="form-select"
            disabled={disabled}
            value={isUnset(fields?.ProxyType) ? "" : fields.ProxyType}
            onChange={(e) => patch({ ProxyType: e.target.value || undefined })}
          >
            <option value="">Nicht konfiguriert</option>
            <option value="None">Kein Proxy</option>
            <option value="Manual">Manuell</option>
            <option value="Auto">Automatisch</option>
          </select>
        </div>
        {fields?.ProxyType === "Manual" ? (
          <>
            <div className="col-md-6">
              <label className="form-label">Proxy-Server</label>
              <input className="form-control" disabled={disabled} value={fields?.ProxyServer || ""} onChange={(e) => patch({ ProxyServer: e.target.value || undefined })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Port</label>
              <input type="number" className="form-control" disabled={disabled} value={fields?.ProxyServerPort ?? ""} onChange={(e) => patch({ ProxyServerPort: e.target.value || undefined })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Proxy-Benutzer</label>
              <input className="form-control" disabled={disabled} value={fields?.ProxyUsername || ""} onChange={(e) => patch({ ProxyUsername: e.target.value || undefined })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Proxy-Passwort</label>
              <input type="password" className="form-control" disabled={disabled} value={fields?.ProxyPassword || ""} onChange={(e) => patch({ ProxyPassword: e.target.value || undefined })} />
            </div>
          </>
        ) : null}
        {fields?.ProxyType === "Auto" ? (
          <div className="col-md-6">
            <label className="form-label">PAC-URL</label>
            <input className="form-control" disabled={disabled} value={fields?.ProxyPACURL || ""} onChange={(e) => patch({ ProxyPACURL: e.target.value || undefined })} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
